import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { EASE } from "./Reveal";

const INITIAL = { name: "", email: "", company: "", message: "" };

export default function ContactModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const close = () => {
    onClose();
    if (state === "sent") setState("idle");
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setState("sent");
      setForm(INITIAL);
    } catch {
      setState("error");
    }
  };

  const inputCls =
    "w-full rounded-[10px] border border-white/[0.14] bg-white/[0.04] px-4 py-3 text-[15px] text-white placeholder-white/35 outline-none transition-colors duration-200 focus:border-brand";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/70 px-4 backdrop-blur-sm"
          onClick={close}
          data-testid="contact-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="relative w-full max-w-lg rounded-2xl border border-white/[0.1] bg-[#0f0f14] p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-modal"
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="contact-modal-close-btn"
              className="absolute right-5 top-5 text-white/50 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            {state === "sent" ? (
              <div className="flex flex-col items-start gap-5 py-4" data-testid="contact-modal-success">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand">
                  <Check className="h-5 w-5 text-white" />
                </span>
                <h3 className="text-[24px] font-medium text-white">Message received.</h3>
                <p className="text-[16px] leading-[1.6] text-[#9a9aa3]">
                  Thanks — we've got your query and we'll get back to you within one business
                  day.
                </p>
                <button
                  onClick={close}
                  data-testid="contact-modal-done-btn"
                  className="mt-2 rounded-[10px] bg-brand px-6 py-3 text-[15px] font-medium text-white transition-colors hover:bg-brand-deep"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-[26px] font-medium tracking-[-0.01em] text-white">
                  Book a call
                </h3>
                <p className="mt-2 text-[15px] leading-[1.6] text-[#9a9aa3]">
                  Tell us about your workflow — we'll reply within one business day.
                </p>
                <form onSubmit={onSubmit} className="mt-7" data-testid="query-form-modal">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your name"
                      data-testid="query-form-name-input"
                      className={inputCls}
                    />
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="Work email"
                      data-testid="query-form-email-input"
                      className={inputCls}
                    />
                  </div>
                  <input
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Company (optional)"
                    data-testid="query-form-company-input"
                    className={`${inputCls} mt-4`}
                  />
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What are you building?"
                    data-testid="query-form-message-input"
                    className={`${inputCls} mt-4 resize-none`}
                  />
                  {state === "error" && (
                    <p className="mt-3 text-[14px] text-red-400" data-testid="query-form-error">
                      Something went wrong — please try again.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    data-testid="query-form-submit-btn"
                    className="group mt-6 inline-flex items-center gap-2 rounded-[10px] bg-brand px-6 py-3.5 text-[15px] font-medium text-white transition-all duration-300 hover:bg-brand-deep disabled:opacity-60"
                  >
                    {state === "sending" ? "Sending…" : "Send the query"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
